import toast from "react-hot-toast";
import type { ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "@/context/UserContext";
import { isNil } from "lodash";
import { useEffect, useMemo } from "react";
import useLogout from "@/hooks/useLogout";
import { useQuery } from "@tanstack/react-query";
import { getUserGuessesPerPeriod } from "@/services/UserService";
import { getGuestPlaysToday } from "@/helpers/helpers";
import { useGuestContext } from "@/context/GuestContext";
import useRegisterCtaModal from "@/hooks/useRegisterCtaModal";
import useCustomLoginWithRedirect from "@/hooks/useCustomLoginWithRedirect";
import config from "@/config";
import CustomSvg from "@/components/common/custom-svg/custom-svg";
import LockedIcon from "@/assets/icons-v2/lock.png";
import useGAEvent from "@/hooks/useGAEvent";
import { triggerShare } from "@/helpers/triggerShare";
import useModalState from "@/hooks/useModalState";
import { useLiveVariant } from "@/context/LiveVariantContext";

interface MenuItemShape {
    label: string;
    url?: string;
    icon: ReactNode;
    isRegistered?: boolean;
    hidden?: boolean;
}

interface SideMenuItemProps {
    item: MenuItemShape;
    onProfileToggle?: () => void;
    onClose: () => void;
}

const SideMenuItem: React.FC<SideMenuItemProps> = (props) => {
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const { handleRegister } = useRegisterCtaModal({ delay: undefined });
    const { guestPlay } = useGuestContext();
    const { isAuthenticated } = useAuth0();
    const { logoutUser } = useLogout();
    const { sendEvent } = useGAEvent();
    const { setModalId } = useModalState();
    const { hasFetchedData, isInvalidLogin, userState, setUserState, clearUserState, fetchUserData, setInvalidLogin } =
        useUser();
    const { liveVariantState, setLiveVariantState } = useLiveVariant();

    const userGuesses = useQuery({
        queryKey: ["user-guesses", userState?.id],
        queryFn: () =>
            getUserGuessesPerPeriod({
                userId: userState?.id as string,
                gameVersion: "classic",
            }),
        gcTime: 0,
        enabled: isAuthenticated && !!userState?.id,
        placeholderData: () => {
            const guestPlayToday = getGuestPlaysToday(guestPlay, userState as any);

            return {
                data: {
                    count: {
                        total: isNil((guestPlayToday as any)?.length) ? 0 : (guestPlayToday as any)?.length,
                    },
                },
            } as any;
        },
    });

    /**
     * React hook to fetch user data when the component mounts and the user is authenticated.
     *
     * @param {boolean} isAuthenticated - Indicates if the user is authenticated (logged in).
     * @param {Object|null} userState - The user object stored in the User context. Can be null if no user data is available.
     * @param {boolean} hasFetchedData - Indicates if the user data has been fetched from the server.
     * @param {Function} fetchUserData - The function to fetch user data.
     */
    useEffect(() => {
        if (isAuthenticated && !isNil(userState) && hasFetchedData) {
            fetchUserData((userState as any).id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    /**
     * React hook to handle invalid login scenarios.
     * If the user is authenticated but has an invalid login (`isInvalidLogin` is true), it logs an error message and initiates a logout process after a 3-second delay.
     *
     * @todo - Move this to a middleware when we use Redux
     * @param {boolean} isAuthenticated - Indicates if the user is authenticated (logged in).
     * @param {boolean} isInvalidLogin - Indicates if the login is invalid (user object in the User context is missing or invalid).
     * @param {Function} logout - The function to initiate the logout process.
     */
    useMemo(() => {
        if (isAuthenticated && isInvalidLogin) {
            setTimeout(() => {
                setInvalidLogin(false);
                logoutUser();
            }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInvalidLogin, logoutUser]);

    /**
     * Checks if the given label corresponds to the 'Switch Location' menu item and if the player has already guessed.
     *
     * @param {string} label - The label or name of the menu item.
     *
     * @returns {boolean} Returns true if the label is 'Switch Location' and the player has already guessed; otherwise, returns false.
     */
    const isLockedSwitchLocation = (label: string) => {
        return (userGuesses as any)?.data?.data?.count?.daily > 0 && label === "Switch Location";
    };

    /**
     * Handles the click event on a side menu item.
     *
     * @param {string} menuItem.label - The label or name of the menu item.
     * @param {string} menuItem.url - The URL to navigate when the menu item is clicked.
     */
    const handleSideMenuItemClick = async ({ label, url }: { label: string; url?: string }) => {
        if (label === "Sign In" && !isAuthenticated) {
            handleRegister(() =>
                customLoginWithRedirect({
                    authorizationParams: {
                        redirect_uri: window.location.origin + "/",
                    },
                    appState: {
                        toggleProfile: true,
                    },
                }),
            );

            return;
        } else if (label === "My Profile") {
            props.onProfileToggle?.();

            return;
        } else if (label === "Sign Out") {
            clearUserState();

            logoutUser();

            return;
        } else if (label === "Invite a friend") {
            sendEvent("invite_friend_menu", {
                isAuthenticated,
                referralCode: localStorage.getItem("referral_code") || undefined,
            });

            // Trigger the share functionality for web and mobile devices
            await triggerShare({
                web: {
                    text: `${config.BASE_URL}/?ref=${localStorage.getItem("referral_code") || ""}`,
                },
                mobile: {
                    title: "PriceMe",
                    text: "Check out PriceMe! It's a home price guessing game. It shows you homes that recently sold near you, and you guess the price:",
                    url: `${config.BASE_URL}/?ref=${localStorage.getItem("referral_code") || ""}`,
                },
            });

            return props.onClose();
        } else if (label === "Switch Location") {
            if (isLockedSwitchLocation(label)) {
                toast.error("You've already started playing today!");
            } else {
                setModalId("SELECT_LOCATION_MODAL");
            }

            return props.onClose();
        }

        if (label === "Choose Area" || label === "Switch Area") {
            // setModalId("LIVE_VARIANT_SELECT_LOCATION_MODAL");
        }

        if (label === "Play archives") {
            setLiveVariantState({ ...liveVariantState, show_archive_select_modal: true });
            return props.onClose();
        }

        if (label.toLowerCase().includes("leaderboard")) {
            // For now puzzle is the only supported version for leaderboard
            setUserState({ ...userState, leaderboardSelectedVersion: "puzzle" });
        }

        if (url) {
            window.location.href = url;
        }
    };

    // Hide menu item if hidden property is set
    if (props.item.hidden) {
        return null;
    }

    if (props.item.label === "Invite a friend" && !isAuthenticated) {
        return null;
    }

    return (
        <li
            className="tw-flex"
            onClick={() => handleSideMenuItemClick(props.item)}
            data-testid={`side-menu-item-${props.item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {isLockedSwitchLocation(props.item.label) ? (
                <CustomSvg src={LockedIcon} size={{ width: 27, height: 27 }} />
            ) : (
                props.item.icon
            )}
            <span className="menu-label">{props.item.label}</span>
        </li>
    );
};

export default SideMenuItem;
